import React, { useEffect, useState } from 'react'
import { Table, Form, Input, Button, Select, Drawer, Space, message, InputNumber, Checkbox } from 'antd'
import adminService from '../../services/adminService'
import AdminPageTitle from '../../components/AdminPageTitle/AdminPageTitle'
import { Helmet } from 'react-helmet-async'

type Option = {
    value: string;
    label: string;
}

type Data = {
    key: string;
    id: number;
    name: string;
    weight: number;
    active: string;
    isActive: boolean;
    parentId?: number;
    parentName?: string;
}

type Props = {}

const AdminCategoriesPage: React.FC<Props> = () => {
  const [loading, setLoading] = useState<boolean>(false)

  const [data, setData] = useState<Data[]>([])
  const [rootCategories, setRootCategories] = useState<Option[]>([])

  const [createForm] = Form.useForm()
  const [showCreateForm, setShowCreateForm] = useState(false)

  const [editForm] = Form.useForm()
  const [editableId, setEditableId] = useState<number>(0)

  const fetchData = () => {
    adminService.getCategories().then((data) => {
      const newData: Data[] = []
      const newRootCategories: Option[] = [{ value: '', label: '' }]
      data.categories.map((c) => {
        newData.push({
          key: c.id.toString(),
          id: c.id,
          name: c.name,
          weight: c.weight,
          parentId: c.parent ? c.parent.id : undefined,
          parentName: c.parent ? c.parent.name : undefined,
          active: c.isActive ? 'да' : 'нет',
          isActive: c.isActive
        })
        if (!c.parent) {
          newRootCategories.push({ value: c.id.toString(), label: c.name })
        }
        return c
      })
      setData(newData)
      setRootCategories(newRootCategories)
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchData()
  }, [])

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Родительская категория',
      dataIndex: 'parentName',
      key: 'parentName'
    },
    {
      title: 'Вес',
      dataIndex: 'weight',
      key: 'weight'
    },
    {
      title: 'Активный',
      dataIndex: 'active',
      key: 'active'
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: Data) => (
                <Space size="middle">
                    <a onClick={() => {
                      const { name, id, parentId, weight, isActive } = record
                      editForm.setFieldValue('name', name)
                      editForm.setFieldValue('weight', weight)
                      editForm.setFieldValue('isActive', isActive)
                      editForm.setFieldValue('parentId', parentId ? parentId.toString() : undefined)
                      setEditableId(id)
                    }}>Изменить</a>
                </Space>
      )
    }
  ]

  const onShowCreateForm = () => {
    setShowCreateForm(true)
  }

  const closeCreateForm = () => {
    setShowCreateForm(false)
  }

  const onSubmitCreateForm = ({ name, weight, parentId, isActive }: any) => {
    isActive = !!isActive
    if (parentId) {
      parentId = parseInt(parentId)
    }
    setLoading(true)
    adminService.createCategory(name, weight, isActive, parentId).then((data) => {
      message.success(`Категория "${data.name}" успешно создана`)
      fetchData()
    }).catch((data) => {
      setLoading(false)
      message.error(data.message)
    })
    setShowCreateForm(false)
    createForm.resetFields()
  }

  const closeEditForm = () => {
    setEditableId(0)
    editForm.resetFields()
  }

  const onSubmitEditForm = ({ name, weight, parentId, isActive }: any) => {
    isActive = !!isActive
    if (editableId === 0) return
    if (parentId) {
      parentId = parseInt(parentId)
    }
    setLoading(true)
    adminService.updateCategory(editableId, name, weight, isActive, parentId).then((data) => {
      message.success(`Категория "${data.name}" успешно отредактированна`)
      fetchData()
    }).catch((data) => {
      setLoading(false)
      message.error(data.message)
    })
    setEditableId(0)
    editForm.resetFields()
  }

  return (
        <div>
            <Helmet><title>Prisma — Категории</title></Helmet>
            <AdminPageTitle title={'Категории'}
                            buttonText={'Новая категория'}
                            onClickButton={onShowCreateForm}/>

            <Table loading={loading} dataSource={data} columns={columns}/>

            <Drawer
                title="Добавить новую категорию"
                width={600}
                onClose={closeCreateForm}
                open={showCreateForm}
                bodyStyle={{ paddingBottom: 80 }}
                extra={
                    <Space>
                        <Button onClick={closeCreateForm}>Отмена</Button>
                        <Button onClick={createForm.submit} type="primary">
                            Отправить
                        </Button>
                    </Space>
                }
            >
                <Form layout="vertical"
                      hideRequiredMark
                      form={createForm}
                      onFinish={onSubmitCreateForm}>
                    <Form.Item label="Название категории"
                               name="name"
                               rules={[{ required: true }]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Родительская категория"
                               name="parentId">
                        <Select options={rootCategories}/>
                    </Form.Item>
                    <Form.Item label="Вес"
                               name="weight"
                               rules={[{ required: true }]}>
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item label="Активный"
                               valuePropName="checked"
                               name="isActive">
                        <Checkbox />
                    </Form.Item>
                </Form>
            </Drawer>

            <Drawer
                title="Редактировать категорию"
                width={600}
                onClose={closeEditForm}
                open={!!editableId}
                bodyStyle={{ paddingBottom: 80 }}
                extra={
                    <Space>
                        <Button onClick={closeEditForm}>Отмена</Button>
                        <Button onClick={editForm.submit} type="primary">
                            Отправить
                        </Button>
                    </Space>
                }
            >
                <Form layout="vertical"
                      hideRequiredMark
                      form={editForm}
                      onFinish={onSubmitEditForm}>
                    <Form.Item label="Название категории"
                               name="name"
                               rules={[{ required: true }]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Родительская категория"
                               name="parentId">
                        <Select options={rootCategories}/>
                    </Form.Item>
                    <Form.Item label="Вес"
                               name="weight"
                               rules={[{ required: true }]}>
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item label="Активный"
                               valuePropName="checked"
                               name="isActive">
                        <Checkbox />
                    </Form.Item>
                </Form>
            </Drawer>

        </div>
  )
}

export default AdminCategoriesPage
